import React, { memo, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';
import Script from 'next/script';

import { useDetectAdBlock } from 'adblock-detect-react';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';
import { getPixallId } from '@atc/pixall-fns';
import { ClientOnly } from '@atc/react-client-only';
import { Page as PageSchema } from '@atc/react-schemas';

import { Col, Row } from 'reaxl';

import { getCompareListingsToggle } from '@/utilities/compareListingsHandler';
import { getFilterHierarchy } from '@/utilities/filterSuggestionHierarchy';
import {
    alphaLeaderBoardContainerOnPositionChange,
    fixedAdWayPointContainerOnPositionChange,
    hideFixedAd,
    showFixedAd,
} from '@/utilities/inventoryAdHelpers';
import usePaidSearchCookies from '@/utilities/usePaidSearchCookies';

import remoteActions from '@/actions/srp/remoteActions';

import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSimilarListingsDuck from '@/ducks/srp/srpSimilarListingsDuck.js';
import srpStrongAttributesDuck from '@/ducks/srp/srpStrongAttributesDuck.js';
import srpSuggestedFiltersDuck from '@/ducks/srp/srpSuggestedFiltersDuck.js';
import userAgentDuck from '@/ducks/userAgentDuck.js';
import userDuck from '@/ducks/userDuck';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';

import useLastSrpUrl from '@/hooks/useLastSrpUrl';

import MyWalletContainer from '@/containers/MyWalletContainer';
import OffsiteNotificationsPrompt from '@/containers/OffsiteNotificationsPrompt';

import { DynamicPlaceholder } from '@/components/DynamicPlaceholder/DynamicPlaceholder';
import EmailSubscriptionToast from '@/components/EmailSubscriptionToast';
import LazyComponent from '@/components/LazyComponent';

import AlphaShowcaseContainer from './AlphaShowcaseContainer';
import AnalyticsWrapper from './AnalyticsWrapper';
import EditorialContentContainer from './EditorialContentContainer';
import EmailSubscribeContainer from './EmailSubscribeContainer';
import ExpiredListingRedirectAlertMessageContainer from './ExpiredListingRedirectAlertMessageContainer';
import FixedAdContainer from './FixedAdContainer';
import FixedAdWaypointContainer from './FixedAdWaypointContainer';
import FooterLeaderAdSlotContainer from './FooterLeaderAdSlotContainer';
import InventoryFiltersContainer from './InventoryFiltersContainer';
import InventorySearchResultsContainer from './InventorySearchResultsContainer';
import ModelReferenceContainer from './ModelReferenceContainer';
import SearchResultsPageModals from './SearchResultsPageModals';
import SearchResultsPageTopActionsContainer from './SearchResultsPageTopActionsContainer';
import SearchResultsTitleContainer from './SearchResultsTitleContainer';
import SrpAlertsContainer from './SrpAlertsContainer';
import SRPAlternativeSearchActionContainer from './SRPAlternativeSearchActionContainer';
import SRPSeoContentModule from './SrpSeoContentContainer';
import SrpShoppingAssistantContainer from './SrpShoppingAssistantContainer';

// lazy load containers
const SRLTowerAdSlotContainer = dynamic(() => import(
    /* webpackChunkName: "SRLTowerAdSlotContainer" */
    './SRLTowerAdSlotContainer'
), { ssr: false });
const CarLoader = dynamic(() => import(
    /* webpackChunkName: "CarLoader" */
    'reaxl'
).then((module) => module.CarLoader), { ssr: false });
// lazy load SRP containers
const CrawlPathFooterContainer = dynamic(() => import(
    /* webpackChunkName: "CrawlPathFooterContainer" */
    '@/containers/srp/CrawlPathFooterContainer'
), { ssr: false });

const maxResultsButtonContainerHeights = { xs: 146, sm: 117, md: 117, lg: 117 };
const MaxResultsButtonContainer = dynamic(() => import(
    /* webpackChunkName: "MaxResultsButtonContainer" */
    '@/containers/srp/MaxResultsButtonContainer'
), {
    loading: () => (<DynamicPlaceholder height={maxResultsButtonContainerHeights} />),
    ssr: false,
});

const saveSearchContainerHeights = { xs: 246, sm: 191, md: 191, lg: 124 };
const SaveSearchContainer = dynamic(() => import(
    /* webpackChunkName: "SaveSearchContainer" */
    '@/containers/srp/SaveSearchContainer'
), {
    loading: () => (<DynamicPlaceholder height={saveSearchContainerHeights} />),
    ssr: false,
});

const buyerConnectHeights = { xs: 523, sm: 391, md: 391, lg: 391 };
const SRPBuyerConnectContainer = dynamic(() => import(
    /* webpackChunkName: "SRPBuyerConnectContainer" */
    '@/containers/srp/SRPBuyerConnectContainer'
), {
    loading: () => (<DynamicPlaceholder height={buyerConnectHeights} />),
    ssr: false,
});

const SRPHeaderContainer = dynamic(() => import(
    /* webpackChunkName: "SRPHeaderContainer" */
    '@/containers/srp/SRPHeaderContainer'
), { ssr: false });

const affixedKbbSRLTowerAdContainerRef = React.createRef();

function SearchResultsPageContainer({ currentUrl }) {
    const {
        alpha_showcase: [isAlphaEnabled],
        brand: [, { channel, inventory_image_url: inventoryImageUrl }],
        enable_still_interested_view_count: [enableStillInterestedViewCount],
        personalization: [isPersonalizationEnabled],
        my_wallet: [enableMyWallet],
        offsite_browser_notifications: [enableOffsiteNotiticationPrompt],
        organization_schema: [isOrganizationSchemaEnabled],
        override_carfax_tile: [isOverrideCarfaxTileEnabled],
        schema: [, { page_schema: pageSchema } = {}],
        shopping_assistant: [enableShoppingAssistant],
        srp_alpha_leader_board: [enableAlphaLeaderBoard],
        srp_bottom_lazy_threshold: [, { defaultThreshold }],
        srp_email_subscribe: [enableEmailSubscribe],
        srp_expert_reviews: [enableExpertReviews],
        srp_fixed_ad_waypoint: [enableFixedAdWaypoint],
        srp_model_reference: [enableModelReference],
        srp_paid_search_cookies: [enablePaidSearchCookies],
        standard_listing_phone_display: [displayPhoneNumber],
        standard_listing_owner_name: [hideOwnerName],
    } = useFeatures([
        'alpha_showcase',
        'brand',
        'enable_still_interested_view_count',
        'personalization',
        'my_wallet',
        'offsite_browser_notifications',
        'organization_schema',
        'override_carfax_tile',
        'schema',
        'shopping_assistant',
        'srp_alpha_leader_board',
        'srp_bottom_lazy_threshold',
        'srp_email_subscribe',
        'srp_expert_reviews',
        'srp_fixed_ad_waypoint',
        'srp_model_reference',
        'srp_paid_search_cookies',
        'standard_listing_phone_display',
        'standard_listing_owner_name',
    ]);
    const dispatch = useDispatch();
    const adBlockDetected = useDetectAdBlock();

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const isBot = useSelector(userAgentDuck.selectors.isBot);

    const showSpinningIndicator = useSelector(srpActiveInteractionDuck.selectors.getShowSpinningIndicator);
    const isSimilarLoading = useSelector(srpSimilarListingsDuck.selectors.getResultLoading);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);
    const isMaxActiveResults = useSelector(srpResultsDuck.selectors.getIsMaxActiveResults);
    const isFiltersVisible = useSelector(srpFiltersDuck.selectors.getFiltersVisible);

    // Filter hierarchy data - calculated once and passed to child components
    const activeFilterValues = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);
    const suggestedFilters = useSelector(srpSuggestedFiltersDuck.selectors.getSuggestedFilters, _isEqual);
    const strongAttributeFilters = useSelector(srpStrongAttributesDuck.selectors.getFormattedStrongAttributesFilters, _isEqual);
    const isTruckSelected = useSelector(srpFiltersDuck.selectors.isTruckSelected);
    const isSUVSelected = useSelector(srpFiltersDuck.selectors.isSUVSelected);
    const featureFilterOptions = useSelector(srpFiltersDuck.selectors.getFeatureFilterOptions, _isEqual);
    const featureFilterValues = useSelector(srpFiltersDuck.selectors.getFeatureFilterValues, _isEqual);
    const cabSizeOptions = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeOptions, _isEqual);
    const cabSizeValues = useSelector(srpFiltersDuck.selectors.getBodyStyleSubtypeCodeValue, _isEqual);

    // Calculate filter hierarchy data once and pass to child components
    const hierarchyData = useMemo(() => {
        const data = getFilterHierarchy({
            activeFilterValues,
            suggestedFilters,
            strongAttributeFilters,
            isTruckSelected,
            isSUVSelected,
            featureFilterOptions,
            featureFilterValues,
            cabSizeOptions,
            cabSizeValues,
        });

        // Determine if any valid filter data exists for display
        const hasValidData = (
            data.isStrongAttribute
            || data.isTruckCard
            || data.isSUVCard
            || data.isVehicleFeatureCard
            || data.isSuggestedFilter
        );

        return {
            ...data,
            hasValidData,
        };
    }, [
        activeFilterValues,
        suggestedFilters,
        strongAttributeFilters,
        isTruckSelected,
        isSUVSelected,
        featureFilterOptions,
        featureFilterValues,
        cabSizeOptions,
        cabSizeValues,
    ]);
    const showToast = useSelector(userDuck.selectors.getShowToast);
    // set kbb paid search cookies
    if (enablePaidSearchCookies) {
        // react hook rules reserve "use" prefixes
        // eslint-disable-next-line react-hooks/rules-of-hooks
        usePaidSearchCookies();
    }

    // Initialize SRP URL tracking
    useLastSrpUrl();

    useEffect(() => {
        const pixAllId = getPixallId() || 'nl';
        if (pixAllId && pixAllId !== 'nl' && !isBot) {
            dispatch(remoteActions.getConsumerBrowsingHistoryData({
                channel,
                pixAllId,
                enableViewCount: enableStillInterestedViewCount,
                displayPhoneNumber,
                hideOwnerName,
                inventoryImageUrl,
            }));
        }

        return () => {
            dispatch(srpActiveInteractionDuck.creators.setKeys({
                showSpinningIndicator: false,
            }));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(userPreferencesDuck.creators.setCompareToggle(getCompareListingsToggle()));
    }, [dispatch]);

    return (
        <>
            {isOverrideCarfaxTileEnabled && !isXs && (
                <Script
                    src="https://snapshot.carfax.com/latest/snapshot.js"
                    strategy="lazyOnload"
                    id="carfax-snapshot-script"
                />
            )}

            {showSpinningIndicator && (
                <div id="page-overlay-container">
                    <CarLoader />
                </div>
            )}

            {enableMyWallet && (
                <MyWalletContainer
                    blockTradeInFetch
                />
            )}

            {enableOffsiteNotiticationPrompt && (
                <OffsiteNotificationsPrompt />
            )}

            <main
                aria-labelledby="search-results-heading"
                className={clsx({ invisible: isFiltersVisible && isXs && !showToast })}
            >

                <SearchResultsTitleContainer />

                <ClientOnly>
                    <SRPHeaderContainer
                        currentUrl={currentUrl}
                    />
                </ClientOnly>

                <SrpAlertsContainer />

                <div className="container">
                    {enableShoppingAssistant && (
                        <ClientOnly>
                            <SrpShoppingAssistantContainer hierarchyData={hierarchyData} />
                        </ClientOnly>
                    )}

                    <div className="margin-horizontal-0 padding-horizontal-0">
                        <FixedAdWaypointContainer
                            cmp="leaderboard"
                            onEnter={showFixedAd}
                            onPositionChange={fixedAdWayPointContainerOnPositionChange}
                        />

                        <Row
                            className="display-sm-flex margin-top-sm-3"
                            ref={affixedKbbSRLTowerAdContainerRef}
                        >

                            <Col
                                lg={3}
                                className="display-none display-lg-block"
                            >
                                <InventoryFiltersContainer />
                                <LazyComponent>
                                    <SRLTowerAdSlotContainer affixedRef={affixedKbbSRLTowerAdContainerRef} />
                                </LazyComponent>
                            </Col>

                            <Col
                                xs={12}
                                lg={9}
                                id="srp-listings"
                            >
                                {(enableEmailSubscribe || isPersonalizationEnabled)
                                    && <EmailSubscribeContainer />}
                                <EmailSubscriptionToast />

                                <SearchResultsPageTopActionsContainer />

                                <ExpiredListingRedirectAlertMessageContainer />

                                {isAlphaEnabled && <AlphaShowcaseContainer />}

                                {isOrganizationSchemaEnabled && <PageSchema pageSchemaData={pageSchema} />}

                                <InventorySearchResultsContainer hierarchyData={hierarchyData} />

                                <SearchResultsPageModals />

                                {enableAlphaLeaderBoard && (
                                    <FixedAdWaypointContainer
                                        cmp="alphaLeaderboard"
                                        onPositionChange={alphaLeaderBoardContainerOnPositionChange}
                                    />
                                )}

                                {enableExpertReviews
                                    && (
                                        <LazyComponent>
                                            <EditorialContentContainer />
                                        </LazyComponent>
                                    )}

                                <SRPAlternativeSearchActionContainer />
                                <SRPSeoContentModule />

                                {!isSimilarLoading && isMaxActiveResults && (
                                    <LazyComponent
                                        height={saveSearchContainerHeights}
                                        threshold={defaultThreshold}
                                    >
                                        <SaveSearchContainer isEmailCapture />
                                    </LazyComponent>
                                )}

                                <LazyComponent
                                    height={maxResultsButtonContainerHeights}
                                    threshold={defaultThreshold}
                                >
                                    <MaxResultsButtonContainer />
                                </LazyComponent>

                                {resultCount < 9 && (
                                    <LazyComponent
                                        height={buyerConnectHeights}
                                        threshold={defaultThreshold}
                                    >
                                        <SRPBuyerConnectContainer position="below" />
                                    </LazyComponent>
                                )}

                                <FooterLeaderAdSlotContainer />
                            </Col>
                        </Row>
                    </div>

                    {!adBlockDetected && (<FixedAdContainer />)}
                    <AnalyticsWrapper />
                </div>

                {enableModelReference
                    && (
                        <ClientOnly fallback={(<div style={{ height: 850 }} />)}>
                            <ModelReferenceContainer />
                        </ClientOnly>
                    )}

                <div className="container">
                    <ClientOnly>
                        <CrawlPathFooterContainer />
                    </ClientOnly>

                    {enableFixedAdWaypoint && (
                        <FixedAdWaypointContainer
                            cmp="leaderboard"
                            onEnter={hideFixedAd}
                            onLeave={showFixedAd}
                        />
                    )}
                </div>
            </main>
        </>
    );
}

export default memo(SearchResultsPageContainer);
